import React from 'react';
import { Languages, Plus, Search, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import i18n from '@/utils/i18n';
import { UserLanguage } from '@/utils/translation';

import ContainerFacetChip, { type FacetOption } from './ContainerFacetChip';
import {
  CORE_FACETS,
  EXTRA_FACETS,
  facetLabel,
  hasAnyFacetValues,
  type ContainerTypeFilterState,
  type FacetKey,
} from './containerTypeFilters';

type ContainerTypesFilterBarProps = {
  filters: ContainerTypeFilterState;
  onFiltersChange: (next: ContainerTypeFilterState) => void;
  language: string;
  onLanguageChange: (language: string) => void;
  facetOptions: (key: FacetKey) => FacetOption[];
  /** Called on any change that should reset pagination / selection. */
  onInteract?: () => void;
};

/**
 * Desktop filter rows: search / language / archived switch, then the faceted
 * chips (Class · Group · Segment · Country) plus the "Add filter" control
 * exposing Size / Branding / Legal owner as removable chips.
 */
export default function ContainerTypesFilterBar({
  filters,
  onFiltersChange,
  language,
  onLanguageChange,
  facetOptions,
  onInteract,
}: ContainerTypesFilterBarProps) {
  const [addFilterOpen, setAddFilterOpen] = React.useState(false);
  const [autoOpenFacet, setAutoOpenFacet] = React.useState<FacetKey | null>(
    null,
  );

  const update = (patch: Partial<ContainerTypeFilterState>) => {
    onFiltersChange({ ...filters, ...patch });
    onInteract?.();
  };

  const setFacet = (key: FacetKey, values: string[]) => {
    update({ facets: { ...filters.facets, [key]: values } });
  };

  const removeExtraFacet = (key: FacetKey) => {
    const facets = { ...filters.facets };
    delete facets[key];
    update({
      facets,
      extraFacets: filters.extraFacets.filter((k) => k !== key),
    });
    setAutoOpenFacet(null);
  };

  const addExtraFacet = (key: FacetKey) => {
    setAddFilterOpen(false);
    setAutoOpenFacet(key);
    update({ extraFacets: [...filters.extraFacets, key] });
  };

  const addableFacets = EXTRA_FACETS.filter(
    (key) => !filters.extraFacets.includes(key),
  );
  const anyFilter =
    hasAnyFacetValues(filters) || filters.extraFacets.length > 0;

  return (
    <div className="tw:flex tw:flex-col tw:gap-3">
      <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2.5">
        <div className="tw:relative tw:w-[340px] tw:max-w-full">
          <Search
            size={16}
            className="tw:pointer-events-none tw:absolute tw:left-3 tw:top-1/2 tw:-translate-y-1/2 tw:text-muted-foreground"
          />
          <Input
            value={filters.search}
            onChange={(e) => update({ search: e.target.value })}
            placeholder={i18n.t('EditContainerInfo:searchPlaceholder')}
            className="tw:pl-9"
          />
        </div>
        <div className="tw:flex-1" />
        <div className="tw:flex tw:items-center tw:gap-2">
          <Languages size={16} className="tw:text-muted-foreground" />
          <Select value={language} onValueChange={onLanguageChange}>
            <SelectTrigger className="tw:w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.keys(UserLanguage).map((key) => (
                <SelectItem key={key} value={key}>
                  {key.toUpperCase()}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="tw:flex tw:items-center tw:gap-2 tw:pl-1.5">
          <span className="tw:text-sm tw:text-muted-foreground">
            {i18n.t('EditContainerInfo:showArchived')}
          </span>
          <Switch
            checked={filters.showArchived}
            onCheckedChange={(checked) => update({ showArchived: checked })}
          />
        </div>
      </div>

      <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
        {CORE_FACETS.map((key) => (
          <ContainerFacetChip
            key={key}
            label={facetLabel(key)}
            options={facetOptions(key)}
            selected={filters.facets[key] ?? []}
            onChange={(values) => setFacet(key, values)}
          />
        ))}
        {filters.extraFacets.map((key) => (
          <ContainerFacetChip
            key={key}
            label={facetLabel(key)}
            options={facetOptions(key)}
            selected={filters.facets[key] ?? []}
            onChange={(values) => setFacet(key, values)}
            defaultOpen={autoOpenFacet === key}
            onRemove={() => removeExtraFacet(key)}
          />
        ))}
        {addableFacets.length > 0 && (
          <Popover open={addFilterOpen} onOpenChange={setAddFilterOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="tw:inline-flex tw:h-9 tw:items-center tw:gap-1.5 tw:rounded-md tw:border-none tw:bg-muted tw:px-3 tw:text-sm tw:font-medium tw:text-foreground tw:outline-none tw:cursor-pointer tw:hover:bg-accent"
              >
                <Plus size={14} />
                {i18n.t('EditContainerInfo:addFilter')}
              </button>
            </PopoverTrigger>
            <PopoverContent
              align="start"
              className="tw:w-[190px] tw:gap-0 tw:p-1.5"
            >
              <div className="tw:px-2 tw:pt-1.5 tw:pb-2 tw:text-xs tw:font-semibold tw:uppercase tw:tracking-wide tw:text-muted-foreground">
                {i18n.t('EditContainerInfo:filterByColumn')}
              </div>
              {addableFacets.map((key) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => addExtraFacet(key)}
                  className="tw:flex tw:w-full tw:items-center tw:gap-2.5 tw:rounded-sm tw:px-2 tw:py-1.5 tw:text-left tw:text-sm tw:text-foreground tw:outline-none tw:cursor-pointer tw:hover:bg-accent"
                >
                  {facetLabel(key)}
                </button>
              ))}
            </PopoverContent>
          </Popover>
        )}
        {anyFilter && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => update({ facets: {}, extraFacets: [] })}
          >
            <X size={13} />
            {i18n.t('EditContainerInfo:clearAll')}
          </Button>
        )}
      </div>
    </div>
  );
}
